import { type FC, useMemo } from "react";

import RequestFeedbackPopupForm from "./RequestFeedbackPopupForm";
import { useForm } from "react-hook-form";
import { Button } from "~/common/components/Button/Button";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { type FeedbackOptionCategory, useOrderListFeedback, type UserData } from "../OrderListFeedbackContext";
import type { OrderData, OrderLineItemData } from "~/OrdersQuotes/common/modelsV2";
import type { MultilevelSelectItemItem } from "~/common/components/Select/MultilevelSelect";
import { getPackagingLabelAtLineItem } from "~/Cart/cartUtils";
import { fakeDescriptorFromProduct } from "~/feature/ProductDescriptor/v1/productDescriptorUtils";
import { CloseBtn } from "~/AddToCart/components/addToCart/AtcCloseButton";
import GridGappedContainer, {
  GridGappedContainerSize,
} from "~/common/components/GridGappedContainer/GridGappedContainer";
import { Col, Row } from "~/common/components/Layout";
import { ParamComponent } from "~/common/components/ParamsGrid";
import { apiSendRequest } from "../../api/SendRequestFeedback";
import Modal from "~/common/components/Modal";
import { Stacker } from "~/common/components/Stacker/Stacker";

interface Props {
  onClose: () => void;
  onSuccess: () => void;
  userData: UserData;
  orderFeedbackOptions: FeedbackOptionCategory[];
  soNumber?: string;
  poNumber?: string;
  orderDetailsEntryData: OrderLineItemData;
  orderData: OrderData;
  lineItemIndex: number;
  subLineNo?: string;
}

const RequestFeedbackPopup: FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const { onClose, onSuccess, subLineNo } = props;
  const orderDetailsEntryData = props.orderDetailsEntryData;
  const soNumber = props.soNumber;
  const poNumber = props.poNumber;
  const { userData } = useOrderListFeedback();

  const { register, control, getValues, handleSubmit, formState } = useForm();

  const dropdownOptions: MultilevelSelectItemItem[] = useMemo(() => {
    return props.orderFeedbackOptions.map((item) => ({
      label: t(item.label),
      value: item.value,
      title: item.title,
      children: item.children.map((child) => ({
        label: t(child.label),
        value: child.value,
        parentValue: item.value,
      })),
    }));
  }, [t, t, props.orderFeedbackOptions]);

  const sendFeedback = () => {
    const translationsMap: Dict<string> = {};

    dropdownOptions.forEach((category) => {
      if (category.children) {
        category.children.forEach((child) => {
          translationsMap[child.value] = category.label + " - " + child.label;
        });
      }
    });

    const formValues = getValues();
    if (soNumber && poNumber) {
      const packagingLabel = getPackagingLabelAtLineItem({
        t: t,
        productDescriptor: fakeDescriptorFromProduct(orderDetailsEntryData.product),
        packCode: orderDetailsEntryData.packCode,
        packSize: orderDetailsEntryData.packSize,
        quantity: subLineNo
          ? orderDetailsEntryData.subEntries.find((subEntry) => subEntry.lineNo === subLineNo)?.confirmedQty
          : orderDetailsEntryData.qty,
      }) as string;

      apiSendRequest({
        comment: formValues.comment,
        subjectOfRequest: translationsMap[formValues.subject],
        subjectCode: formValues.subject,
        soNumber: soNumber,
        poNumber: poNumber,
        orderDetailsEntryData: orderDetailsEntryData,
        packagingLabel: packagingLabel,
        salesOrg: props.orderData.account?.salesOrg ?? undefined,
        accountNumber: props.orderData.account?.code ?? undefined,
        subLineNo: subLineNo,
      }).then((result) => {
        if (result) {
          onSuccess();
        }
      });
    }
  };

  return (
    <Modal
      onClose={onClose}
      modalContentClassName={"disable-horizontal-scroll"}
      title={t("serviceCases.addCase.header")}
      footerChildren={
        <div className="d-flex flex-justify-between flex-align-center w-100">
          <p>{t("linear.request.quote.popup.mandatory")}</p>
          <Stacker direction={"row"} gap={8}>
            <Button size={"medium"} variant={"text"} onClick={onClose}>
              {t("cancel")}
            </Button>
            <Button disabled={!formState.isValid} onClick={handleSubmit(sendFeedback)}>
              {t("serviceCases.button.create")}
            </Button>
          </Stacker>
        </div>
      }
    >
      <Row>
        <Col size={4}>
          <ParamComponent
            label={t("request.information.order.line")}
            value={subLineNo ?? orderDetailsEntryData.lineNo}
          />
        </Col>
        <Col size={4}>
          <ParamComponent label={t("request.information.sales.order")} value={soNumber} />
        </Col>
      </Row>
      <Row>
        <Col size={4}>
          <ParamComponent label={t("request.information.name")} value={`${userData.firstName} ${userData.lastName}`} />
        </Col>
        <Col size={4}>
          <ParamComponent label={t("request.information.email")} value={userData.email} />
        </Col>
        <Col size={4}>
          <ParamComponent label={t("request.information.phone")} value={userData.phone ? userData.phone : "-"} />
        </Col>
      </Row>
      <Row className={"mt-3 mb-3"}>
        <Col size={12}>
          <RequestFeedbackPopupForm register={register} control={control} dropdownOptions={dropdownOptions} />
        </Col>
      </Row>
    </Modal>
  );
};

function fieldIsEmpty(fieldValue: string): boolean {
  return !fieldValue || fieldValue.trim() === "";
}

export default RequestFeedbackPopup;
